import { IS_PRODUCTION } from '@repo/common-utils';
import { useAuthContext } from '@repo/user-session';
import fetch from 'isomorphic-fetch';
import { FetchFunction, UploadableMap } from 'relay-runtime';

const buildFormData = (query: string, variables: unknown, uploadables: UploadableMap) => {
  const formData = new FormData();
  const map: Record<string, string[]> = {};
  const uploadableEntries = Object.entries(uploadables);
  const isSingleFileCase = uploadableEntries.length === 1 && uploadableEntries[0]?.[0] !== '0';

  formData.append(
    'operations',
    JSON.stringify({
      query: query.replace(/\n/g, ''),
      variables
    })
  );

  if (isSingleFileCase) {
    uploadableEntries.forEach(([field, file]) => {
      map['0'] = [`variables.${field}`];
      formData.append('map', JSON.stringify(map));
      if (file) {
        formData.append('0', file);
      }
    });

    return formData;
  }

  uploadableEntries.forEach(([key]) => {
    map[key] = [`variables.append.${key}`];
  });

  formData.append('map', JSON.stringify(map));

  uploadableEntries.forEach(([key, file]) => {
    if (file) {
      formData.append(key, file);
    }
  });

  return formData;
};

const useFetchRelayFactory = (token: string, zentSessionId: string, zentClientSessionId: string) => {
  const { setStatusSignOut } = useAuthContext();

  const fetchRelay: FetchFunction = async (params, variables, _, uploadables) => {
    if (!IS_PRODUCTION && process.env.NEXT_PUBLIC_TRACKING_DEBUG_MODE === 'true') {
      console.info('-QUERY_FETCH_LOG:', params.name, variables);
    }

    const apiServer = process.env.NEXT_PUBLIC_GQL_API_SERVER ?? '';
    const hasUploadables = Boolean(uploadables && Object.keys(uploadables).length > 0);
    const headers: Record<string, string> = {};

    if (token) {
      headers.Authorization = `Bearer ${token}`;
      headers['X-Zent-Session-Id'] = `${zentSessionId}`;
      headers['X-Zent-Client-Session-Id'] = `${zentClientSessionId}`;
    }

    if (hasUploadables) {
      headers['apollo-require-preflight'] = 'true';
    } else {
      headers['Content-Type'] = 'application/json';
    }

    try {
      const response = await fetch(apiServer, {
        method: 'POST',
        headers,
        body: hasUploadables
          ? buildFormData(params.text as string, variables, uploadables ?? {})
          : JSON.stringify({
              query: params.text as string,
              variables
            })
      });

      const result = await response.json();
      if (result.errors) {
        if (result.errors.some(({ code }: { code: string }) => code === 'FORBIDDEN')) {
          console.error('Access forbidden. Clearing access token and reloading the page.');
          setStatusSignOut();
          return;
        }
      }

      return result;
    } catch (error: unknown) {
      console.error('fetchGraphQL error:', error);
      return new Promise((resolve) => {
        resolve({ data: {} });
      });
    }
  };

  return fetchRelay;
};

export default useFetchRelayFactory;
